// =============================================================================
// WIDGET TICKETS - Gouvernance OmniHub
// =============================================================================
// Interface complète de gestion des tickets OH (Ticket First Development)
// - Liste filtrable
// - Vue Kanban drag & drop
// - Création avec validation scope
// - Détail + historique
// - Distinction BUSINESS / TECHNICAL
// =============================================================================

import { useState, useEffect, useCallback } from "react";
import {
  Plus,
  Search,
  Filter,
  LayoutGrid,
  List,
  Ticket,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  PlayCircle,
  PauseCircle,
  Eye,
  ChevronRight,
  ChevronDown,
  RefreshCw,
  Loader2,
  FileCode,
  Briefcase,
  Tag,
  User,
  Calendar,
  GitBranch,
  Shield,
  TestTube,
  ArrowRight,
  GripVertical,
  X
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Badge } from "../../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Textarea } from "../../../components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL;

// Configuration des statuts
const STATUSES = [
  { id: "draft", label: "Draft", color: "text-zinc-400", bg: "bg-zinc-500/10", border: "border-zinc-500/30", icon: FileCode },
  { id: "ready", label: "Ready", color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/30", icon: CheckCircle2 },
  { id: "in_progress", label: "In Progress", color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/30", icon: PlayCircle },
  { id: "review", label: "Review", color: "text-purple-400", bg: "bg-purple-500/10", border: "border-purple-500/30", icon: Eye },
  { id: "blocked", label: "Blocked", color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/30", icon: XCircle },
  { id: "done", label: "Done", color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/30", icon: CheckCircle2 }
];

const PRIORITIES = [
  { id: "critical", label: "Critical", color: "text-red-400", bg: "bg-red-500/20" },
  { id: "high", label: "High", color: "text-orange-400", bg: "bg-orange-500/20" },
  { id: "medium", label: "Medium", color: "text-blue-400", bg: "bg-blue-500/20" },
  { id: "low", label: "Low", color: "text-zinc-400", bg: "bg-zinc-500/20" }
];

const TYPES = [
  { id: "feature", label: "Feature", icon: Plus, color: "text-emerald-400" },
  { id: "bug", label: "Bug", icon: AlertTriangle, color: "text-red-400" },
  { id: "improvement", label: "Improvement", icon: ArrowRight, color: "text-blue-400" },
  { id: "refactor", label: "Refactor", icon: GitBranch, color: "text-purple-400" },
  { id: "documentation", label: "Documentation", icon: FileCode, color: "text-zinc-400" }
];

// Catégories BUSINESS vs TECHNICAL
const CATEGORIES = [
  { id: "business", label: "BUSINESS", icon: Briefcase, color: "text-emerald-400", bg: "bg-emerald-500/10" },
  { id: "technical", label: "TECHNICAL", icon: FileCode, color: "text-blue-400", bg: "bg-blue-500/10" }
];

export default function GouvTickets({ onRefreshStats }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(() => (typeof window !== "undefined" && window.innerWidth < 768 ? "list" : "kanban")); // "kanban" | "list" — par défaut liste sur mobile
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    type: "feature",
    priority: "medium",
    category: "technical",
    modules_impacted: "",
    tests_required: "",
    acceptance_criteria: "",
    assigned_to: ""
  });
  const [formErrors, setFormErrors] = useState([]);
  const [creating, setCreating] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Charger les tickets
  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/oh/tickets`);
      if (res.ok) {
        const data = await res.json();
        setTickets(data.tickets || []);
      }
    } catch (err) {
      console.error("Erreur chargement tickets:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  // Filtrer les tickets
  const filteredTickets = tickets.filter(ticket => {
    if (searchQuery && !ticket.title?.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !ticket.reference?.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (filterStatus !== "all" && ticket.status !== filterStatus) return false;
    if (filterPriority !== "all" && ticket.priority !== filterPriority) return false;
    // Category filter based on type
    if (filterCategory === "business" && !["feature", "improvement"].includes(ticket.type)) return false;
    if (filterCategory === "technical" && !["bug", "refactor", "documentation"].includes(ticket.type)) return false;
    return true;
  });

  // Grouper par statut pour Kanban
  const ticketsByStatus = STATUSES.reduce((acc, status) => {
    acc[status.id] = filteredTickets.filter(t => t.status === status.id);
    return acc;
  }, {});

  // Créer un ticket
  const handleCreate = async () => {
    setFormErrors([]);
    setCreating(true);
    
    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        type: formData.type,
        priority: formData.priority,
        modules_impacted: formData.modules_impacted.split(",").map(s => s.trim()).filter(Boolean),
        tests_required: formData.tests_required.split(",").map(s => s.trim()).filter(Boolean),
        acceptance_criteria: formData.acceptance_criteria.split("\n").map(s => s.trim()).filter(Boolean),
        assigned_to: formData.assigned_to || null
      };
      
      const res = await fetch(`${API}/api/oh/tickets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        setShowCreateModal(false);
        resetForm();
        loadTickets();
        onRefreshStats?.();
      } else {
        // Afficher les erreurs de validation
        if (data.validation?.blocking_issues) {
          setFormErrors(data.validation.blocking_issues);
        } else if (data.detail) {
          setFormErrors([data.detail]);
        }
      }
    } catch (err) {
      setFormErrors(["Erreur de connexion"]);
    } finally {
      setCreating(false);
    }
  };

  // Changer le statut
  const handleStatusChange = async (ticketId, newStatus, note = "") => {
    setUpdatingStatus(true);
    try {
      const res = await fetch(`${API}/api/oh/tickets/${ticketId}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus, note })
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        loadTickets();
        onRefreshStats?.();
        if (selectedTicket?.id === ticketId) {
          setSelectedTicket({ ...selectedTicket, status: newStatus });
        }
      } else {
        // Afficher l'erreur (ticket bloqué, etc.)
        alert(data.message || data.error || "Impossible de changer le statut");
      }
    } catch (err) {
      console.error("Erreur changement statut:", err);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      type: "feature",
      priority: "medium",
      category: "technical",
      modules_impacted: "",
      tests_required: "",
      acceptance_criteria: "",
      assigned_to: ""
    });
    setFormErrors([]);
  };

  const getStatusConfig = (status) => STATUSES.find(s => s.id === status) || STATUSES[0];
  const getPriorityConfig = (priority) => PRIORITIES.find(p => p.id === priority) || PRIORITIES[2];
  const getTypeConfig = (type) => TYPES.find(t => t.id === type) || TYPES[0];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Ticket className="w-5 h-5 text-blue-400" />
            Tickets OH
          </h2>
          <p className="text-sm text-zinc-500 mt-1">
            {tickets.length} ticket(s) • {filteredTickets.length} affiché(s)
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <Button
            onClick={() => loadTickets()}
            variant="outline"
            size="sm"
            className="bg-zinc-800 border-zinc-700 hover:bg-zinc-700"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="gouv-tickets-create"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau Ticket
          </Button>
        </div>
      </div>

      {/* Filtres et vue */}
      <div className="flex flex-wrap items-center gap-2 md:gap-3 bg-zinc-900/50 p-3 md:p-4 rounded-xl border border-zinc-800">
        {/* Recherche */}
        <div className="relative w-full md:w-auto md:flex-1 md:max-w-xs order-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher..."
            className="pl-9 bg-zinc-800 border-zinc-700"
          />
        </div>

        {/* Filtre statut */}
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-32 md:w-40 bg-zinc-800 border-zinc-700 order-2">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-700">
            <SelectItem value="all">Tous les statuts</SelectItem>
            {STATUSES.map(s => (
              <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Filtre priorité */}
        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="w-28 md:w-36 bg-zinc-800 border-zinc-700 order-3">
            <SelectValue placeholder="Priorité" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-700">
            <SelectItem value="all">Toutes</SelectItem>
            {PRIORITIES.map(p => (
              <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Filtre catégorie — scroll horizontal sur mobile pour ne pas déborder */}
        <div className="flex items-center gap-1 bg-zinc-800 rounded-lg p-1 overflow-x-auto order-5 md:order-4 w-full md:w-auto">
          <button
            onClick={() => setFilterCategory("all")}
            className={`px-3 py-1.5 rounded text-sm transition-colors shrink-0 ${
              filterCategory === "all" ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-white'
            }`}
          >
            Tous
          </button>
          {CATEGORIES.map(cat => (
            <button
              key={cat.id}
              onClick={() => setFilterCategory(cat.id)}
              className={`px-3 py-1.5 rounded text-sm transition-colors flex items-center gap-1 shrink-0 ${
                filterCategory === cat.id ? `${cat.bg} ${cat.color}` : 'text-zinc-400 hover:text-white'
              }`}
            >
              <cat.icon className="w-3 h-3" />
              {cat.label}
            </button>
          ))}
        </div>

        {/* Toggle vue */}
        <div className="flex items-center gap-1 bg-zinc-800 rounded-lg p-1 order-4 md:order-5 ml-auto">
          <button
            onClick={() => setViewMode("kanban")}
            className={`p-2 rounded transition-colors ${
              viewMode === "kanban" ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-white'
            }`}
            title="Vue Kanban"
          >
            <LayoutGrid className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={`p-2 rounded transition-colors ${
              viewMode === "list" ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-white'
            }`}
            title="Vue Liste"
          >
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Contenu */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
        </div>
      ) : viewMode === "kanban" ? (
        /* VUE KANBAN */
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-4 pb-4">
          {STATUSES.map(status => (
            <div key={status.id} className="min-w-0">
              {/* Header colonne */}
              <div className={`flex items-center gap-2 px-3 py-2 rounded-t-xl ${status.bg} border-b ${status.border}`}>
                <status.icon className={`w-4 h-4 ${status.color}`} />
                <span className={`text-sm font-medium ${status.color}`}>{status.label}</span>
                <Badge variant="secondary" className="ml-auto text-xs">
                  {ticketsByStatus[status.id]?.length || 0}
                </Badge>
              </div>
              
              {/* Tickets */}
              <div className="space-y-2 p-2 bg-zinc-900/30 rounded-b-xl min-h-[300px] border border-t-0 border-zinc-800">
                {ticketsByStatus[status.id]?.map(ticket => (
                  <TicketCard
                    key={ticket.id}
                    ticket={ticket}
                    onSelect={() => {
                      setSelectedTicket(ticket);
                      setShowDetailModal(true);
                    }}
                    getStatusConfig={getStatusConfig}
                    getPriorityConfig={getPriorityConfig}
                    getTypeConfig={getTypeConfig}
                  />
                ))}
                {ticketsByStatus[status.id]?.length === 0 && (
                  <div className="text-center py-8 text-zinc-600 text-sm">
                    Aucun ticket
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* VUE LISTE */
        <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 overflow-x-auto">
          <table className="w-full min-w-[880px]">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-500 uppercase">Référence</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-500 uppercase">Titre</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-500 uppercase">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-500 uppercase">Priorité</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-500 uppercase">Statut</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-500 uppercase">Modules</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTickets.map(ticket => {
                const statusConfig = getStatusConfig(ticket.status);
                const priorityConfig = getPriorityConfig(ticket.priority);
                const typeConfig = getTypeConfig(ticket.type);
                
                return (
                  <tr 
                    key={ticket.id}
                    className="border-b border-zinc-800/50 hover:bg-zinc-800/30 cursor-pointer transition-colors"
                    onClick={() => {
                      setSelectedTicket(ticket);
                      setShowDetailModal(true);
                    }}
                  >
                    <td className="px-4 py-3">
                      <span className="text-sm font-mono text-blue-400">{ticket.reference}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-white">{ticket.title}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <typeConfig.icon className={`w-3 h-3 ${typeConfig.color}`} />
                        <span className={`text-xs ${typeConfig.color}`}>{typeConfig.label}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={`${priorityConfig.bg} ${priorityConfig.color} text-xs`}>
                        {priorityConfig.label}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={`${statusConfig.bg} ${statusConfig.color} ${statusConfig.border} border text-xs`}>
                        {statusConfig.label}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {ticket.modules_impacted?.slice(0, 2).map(m => (
                          <Badge key={m} variant="secondary" className="text-xs">
                            {m}
                          </Badge>
                        ))}
                        {ticket.modules_impacted?.length > 2 && (
                          <Badge variant="secondary" className="text-xs">
                            +{ticket.modules_impacted.length - 2}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Button variant="ghost" size="sm" onClick={(e) => e.stopPropagation()}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredTickets.length === 0 && (
            <div className="text-center py-12 text-zinc-500">
              Aucun ticket trouvé
            </div>
          )}
        </div>
      )}

      {/* Modal Création */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus className="w-5 h-5 text-blue-400" />
              Nouveau Ticket OH
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Titre */}
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Titre *
              </label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ex: Intégrer autocomplétion adresse"
                className="bg-zinc-800 border-zinc-700"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Description * (min 20 caractères)
              </label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description détaillée du besoin..."
                className="bg-zinc-800 border-zinc-700 min-h-[100px]"
              />
            </div>

            {/* Type + Priorité */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Type</label>
                <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    {TYPES.map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        <div className="flex items-center gap-2">
                          <t.icon className={`w-4 h-4 ${t.color}`} />
                          {t.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Priorité</label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    {PRIORITIES.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        <Badge className={`${p.bg} ${p.color}`}>{p.label}</Badge>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Modules impactés */}
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Modules impactés * (séparés par virgule)
              </label>
              <Input
                value={formData.modules_impacted}
                onChange={(e) => setFormData({ ...formData, modules_impacted: e.target.value })}
                placeholder="client, dossier, devis, communication..."
                className="bg-zinc-800 border-zinc-700"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Ex: client, dossier, devis, email, rbac
              </p>
            </div>

            {/* Tests requis */}
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Tests requis * (séparés par virgule)
              </label>
              <Input
                value={formData.tests_required}
                onChange={(e) => setFormData({ ...formData, tests_required: e.target.value })}
                placeholder="test_address_autocomplete, test_client_form..."
                className="bg-zinc-800 border-zinc-700"
              />
            </div>

            {/* Critères d'acceptation */}
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Critères d'acceptation * (un par ligne)
              </label>
              <Textarea
                value={formData.acceptance_criteria}
                onChange={(e) => setFormData({ ...formData, acceptance_criteria: e.target.value })}
                placeholder="L'autocomplétion active après 3 caractères&#10;Le code postal se remplit automatiquement&#10;..."
                className="bg-zinc-800 border-zinc-700 min-h-[100px]"
              />
            </div>

            {/* Erreurs */}
            {formErrors.length > 0 && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                <p className="text-sm font-medium text-red-400 mb-2">Validation échouée :</p>
                <ul className="text-sm text-red-300 space-y-1">
                  {formErrors.map((err, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      {err}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)} className="bg-zinc-800 border-zinc-700">
              Annuler
            </Button>
            <Button onClick={handleCreate} disabled={creating} className="bg-blue-600 hover:bg-blue-700">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Créer le ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Détail */}
      <TicketDetailModal
        ticket={selectedTicket}
        open={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedTicket(null);
        }}
        onStatusChange={handleStatusChange}
        updatingStatus={updatingStatus}
        getStatusConfig={getStatusConfig}
        getPriorityConfig={getPriorityConfig}
        getTypeConfig={getTypeConfig}
      />
    </div>
  );
}

// =============================================================================
// Composant TicketCard (Kanban)
// =============================================================================
function TicketCard({ ticket, onSelect, getStatusConfig, getPriorityConfig, getTypeConfig }) {
  const priorityConfig = getPriorityConfig(ticket.priority);
  const typeConfig = getTypeConfig(ticket.type);
  
  const isBusinessType = ["feature", "improvement"].includes(ticket.type);
  
  return (
    <div
      onClick={onSelect}
      className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700/50 hover:border-zinc-600 cursor-pointer transition-all hover:shadow-lg group"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-mono text-blue-400">{ticket.reference}</span>
        <Badge className={`${priorityConfig.bg} ${priorityConfig.color} text-[10px]`}>
          {priorityConfig.label}
        </Badge>
      </div>
      
      {/* Titre */}
      <h4 className="text-sm font-medium text-white mb-2 line-clamp-2 group-hover:text-blue-300 transition-colors">
        {ticket.title}
      </h4>
      
      {/* Footer */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Type */}
          <div className={`flex items-center gap-1 text-xs ${typeConfig.color}`}>
            <typeConfig.icon className="w-3 h-3" />
          </div>
          {/* Business/Technical badge */}
          <Badge 
            variant="secondary" 
            className={`text-[10px] ${isBusinessType ? 'bg-emerald-500/10 text-emerald-400' : 'bg-blue-500/10 text-blue-400'}`}
          >
            {isBusinessType ? 'BUS' : 'TECH'}
          </Badge>
        </div>
        
        {/* Modules count */}
        {ticket.modules_impacted?.length > 0 && (
          <span className="text-[10px] text-zinc-500">
            {ticket.modules_impacted.length} module(s)
          </span>
        )}
      </div>
      
      {/* Indicateurs validation */}
      <div className="flex items-center gap-2 mt-2 pt-2 border-t border-zinc-700/50">
        {ticket.e2e_test_result?.passed !== undefined && (
          <div className={`flex items-center gap-1 text-[10px] ${
            ticket.e2e_test_result.passed ? 'text-emerald-400' : 'text-red-400'
          }`}>
            <TestTube className="w-3 h-3" />
            E2E
          </div>
        )}
        {ticket.groot_scan_result?.passed !== undefined && (
          <div className={`flex items-center gap-1 text-[10px] ${
            ticket.groot_scan_result.passed ? 'text-emerald-400' : 'text-red-400'
          }`}>
            <Shield className="w-3 h-3" />
            Groot
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Modal Détail Ticket
// =============================================================================
function TicketDetailModal({ 
  ticket, 
  open, 
  onClose, 
  onStatusChange,
  updatingStatus,
  getStatusConfig, 
  getPriorityConfig, 
  getTypeConfig 
}) {
  if (!ticket) return null;
  
  const statusConfig = getStatusConfig(ticket.status);
  const priorityConfig = getPriorityConfig(ticket.priority);
  const typeConfig = getTypeConfig(ticket.type);
  const isBusinessType = ["feature", "improvement"].includes(ticket.type);
  
  // Déterminer les transitions possibles
  const getNextStatuses = () => {
    switch (ticket.status) {
      case "draft": return ["ready"];
      case "ready": return ["in_progress"];
      case "in_progress": return ["review"];
      case "review": return ["done"];
      case "blocked": return ["in_progress", "ready"];
      default: return [];
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-700 max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-lg font-mono text-blue-400">{ticket.reference}</span>
              <Badge className={`${statusConfig.bg} ${statusConfig.color} ${statusConfig.border} border`}>
                {statusConfig.label}
              </Badge>
              <Badge className={`${priorityConfig.bg} ${priorityConfig.color}`}>
                {priorityConfig.label}
              </Badge>
            </div>
          </div>
          <DialogTitle className="text-white text-xl mt-2">{ticket.title}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Badges catégorie */}
          <div className="flex items-center gap-2">
            <Badge className={`${isBusinessType ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' : 'bg-blue-500/10 text-blue-400 border-blue-500/30'} border`}>
              {isBusinessType ? 'BUSINESS' : 'TECHNICAL'}
            </Badge>
            <div className={`flex items-center gap-1 ${typeConfig.color}`}>
              <typeConfig.icon className="w-4 h-4" />
              <span className="text-sm">{typeConfig.label}</span>
            </div>
          </div>
          
          {/* Description */}
          <div>
            <h4 className="text-sm font-semibold text-zinc-400 mb-2">Description</h4>
            <p className="text-sm text-zinc-300 whitespace-pre-wrap">{ticket.description}</p>
          </div>
          
          {/* Modules impactés */}
          <div>
            <h4 className="text-sm font-semibold text-zinc-400 mb-2">Modules impactés</h4>
            <div className="flex flex-wrap gap-2">
              {ticket.modules_impacted?.map(m => (
                <Badge key={m} variant="secondary">{m}</Badge>
              ))}
              {!ticket.modules_impacted?.length && (
                <span className="text-sm text-zinc-500">Non définis</span>
              )}
            </div>
          </div>
          
          {/* Tests requis */}
          <div>
            <h4 className="text-sm font-semibold text-zinc-400 mb-2">Tests requis</h4>
            <div className="flex flex-wrap gap-2">
              {ticket.tests_required?.map(t => (
                <Badge key={t} variant="outline" className="border-zinc-700 text-zinc-300">{t}</Badge>
              ))}
              {!ticket.tests_required?.length && (
                <span className="text-sm text-zinc-500">Non définis</span>
              )}
            </div>
          </div>
          
          {/* Critères d'acceptation */}
          <div>
            <h4 className="text-sm font-semibold text-zinc-400 mb-2">Critères d'acceptation</h4>
            <ul className="space-y-1">
              {ticket.acceptance_criteria?.map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                  <CheckCircle2 className="w-4 h-4 text-zinc-500 mt-0.5 flex-shrink-0" />
                  {c}
                </li>
              ))}
              {!ticket.acceptance_criteria?.length && (
                <span className="text-sm text-zinc-500">Non définis</span>
              )}
            </ul>
          </div>
          
          {/* Résultats validation */}
          {(ticket.e2e_test_result || ticket.groot_scan_result) && (
            <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
              <h4 className="text-sm font-semibold text-zinc-400 mb-3">Résultats de validation</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {ticket.e2e_test_result && (
                  <div className={`flex items-center gap-2 ${ticket.e2e_test_result.passed ? 'text-emerald-400' : 'text-red-400'}`}>
                    <TestTube className="w-5 h-5" />
                    <span>Tests E2E: {ticket.e2e_test_result.passed ? 'Passés' : 'Échoués'}</span>
                  </div>
                )}
                {ticket.groot_scan_result && (
                  <div className={`flex items-center gap-2 ${ticket.groot_scan_result.passed ? 'text-emerald-400' : 'text-red-400'}`}>
                    <Shield className="w-5 h-5" />
                    <span>Groot: {ticket.groot_scan_result.passed ? 'OK' : 'Violations'}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Historique / Notes */}
          {ticket.notes?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-zinc-400 mb-2">Historique</h4>
              <div className="space-y-2">
                {ticket.notes.map((note, i) => (
                  <div key={i} className="bg-zinc-800/30 rounded-lg p-3 border border-zinc-700/50">
                    <div className="flex items-center gap-2 text-xs text-zinc-500 mb-1">
                      <Clock className="w-3 h-3" />
                      {new Date(note.timestamp).toLocaleString("fr-FR")}
                      <span className="text-zinc-600">•</span>
                      <span>{note.status_from} → {note.status_to}</span>
                    </div>
                    <p className="text-sm text-zinc-300">{note.note}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Timestamps */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-zinc-500">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>Créé: {ticket.created_at ? new Date(ticket.created_at).toLocaleDateString("fr-FR") : '-'}</span>
            </div>
            {ticket.started_at && (
              <div className="flex items-center gap-2">
                <PlayCircle className="w-4 h-4" />
                <span>Démarré: {new Date(ticket.started_at).toLocaleDateString("fr-FR")}</span>
              </div>
            )}
            {ticket.completed_at && (
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                <span>Terminé: {new Date(ticket.completed_at).toLocaleDateString("fr-FR")}</span>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* Boutons de transition */}
            {getNextStatuses().map(nextStatus => {
              const nextConfig = getStatusConfig(nextStatus);
              return (
                <Button
                  key={nextStatus}
                  onClick={() => onStatusChange(ticket.id, nextStatus)}
                  disabled={updatingStatus}
                  className={`${nextConfig.bg} ${nextConfig.color} border ${nextConfig.border} hover:opacity-80`}
                  variant="outline"
                >
                  {updatingStatus ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <ArrowRight className="w-4 h-4 mr-2" />
                  )}
                  → {nextConfig.label}
                </Button>
              );
            })}
          </div>
          <Button variant="outline" onClick={onClose} className="bg-zinc-800 border-zinc-700">
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
